import React from 'react';
import { useDispatch } from 'react-redux';
import styles from './ServiceEditModal.module.scss';
import { Form, Formik } from 'formik';
import { Button } from '@mui/material';
import { salonManagementActions } from '../../../../store';
import * as yup from 'yup';
import ButtonSelector from '../../../../../Common/CustomSelector/ButtonSelector';
import clsx from 'clsx';
import useWindowSize from '../../../../../../utils/useWindowSize';
import { useTranslation } from 'react-i18next';

const sanitizePriceInput = (raw) => {
  let value = String(raw ?? '').replace(',', '.');
  value = value.replace(/[^\d.]/g, '');
  const firstDot = value.indexOf('.');
  if (firstDot !== -1) {
    value =
      value.slice(0, firstDot + 1) +
      value.slice(firstDot + 1).replace(/\./g, '');
    const [intPart, decPart = ''] = value.split('.');
    value = `${intPart}.${decPart.slice(0, 2)}`;
  }
  return value;
};

const parsePrice = (raw) => {
  if (raw === '' || raw == null) return undefined;
  const n = Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
};

const isPriceKeyAllowed = (key, currentValue) => {
  if (
    [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ].includes(key)
  ) {
    return true;
  }
  if (/^\d$/.test(key)) return true;
  if ((key === '.' || key === ',') && !String(currentValue || '').includes('.')) {
    return true;
  }
  return false;
};

export default ({ closeModal, service }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();

  const servicesTimesInMinutes = [
    { value: 15, name: t('workers.editServiceModal.times.15min') },
    { value: 20, name: t('workers.editServiceModal.times.20min') },
    { value: 25, name: t('workers.editServiceModal.times.25min') },
    { value: 30, name: t('workers.editServiceModal.times.30min') },
    { value: 35, name: t('workers.editServiceModal.times.35min') },
    { value: 40, name: t('workers.editServiceModal.times.40min') },
    { value: 45, name: t('workers.editServiceModal.times.45min') },
    { value: 50, name: t('workers.editServiceModal.times.50min') },
    { value: 55, name: t('workers.editServiceModal.times.55min') },
    { value: 60, name: t('workers.editServiceModal.times.60min') },
  ];

  const validationSchema = {
    cost: yup
      .number()
      .transform((_value, originalValue) => parsePrice(originalValue))
      .min(4, t('workers.editServiceModal.validation.priceMin'))
      .required(t('workers.editServiceModal.validation.priceFieldRequired'))
      .typeError(t('workers.editServiceModal.validation.priceTypeError'))
      .test(
        'max-decimals',
        t('workers.editServiceModal.validation.maxDecimals'),
        (value) =>
          value === undefined ||
          value === null ||
          Number.isInteger(value) ||
          /^\d+(\.\d{1,2})?$/.test(String(value)),
      ),
  };

  const initialValues = {
    id: service.id,
    duration: service.duration,
    cost: service.cost != null && service.cost !== '' ? String(service.cost) : '',
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={yup.object().shape(validationSchema)}
      onSubmit={(values) => {
        const req = {
          idServiziPoltrona: values.id,
          time: values.duration,
          prezzo: parsePrice(values.cost),
        };
        dispatch(salonManagementActions.MOD_WORKER_SERVICE.REQUEST(req, closeModal));
      }}
    >
      {({ values, setFieldValue, setFieldTouched, errors, touched }) => {
        const priceError = touched.cost && errors.cost;

        return (
          <Form
            className={clsx(styles.service_modal_wrapper, {
              [styles.service_modal_wrapper_mobile]: isMobile,
            })}
          >
            <div className={styles.body}>
              <ButtonSelector
                value={values.duration}
                setValue={(value) => setFieldValue('duration', value)}
                options={servicesTimesInMinutes}
                title={t('workers.editServiceModal.duration')}
                marginLeft={0}
                height="48px"
                margin="0 0 16px 0"
                width="100%"
              />
              <div className={styles.price_field}>
                <label className={styles.price_label} htmlFor="worker-service-cost">
                  {t('workers.editServiceModal.price')}
                </label>
                <div
                  className={clsx(styles.price_control, {
                    [styles.price_control_error]: !!priceError,
                  })}
                >
                  <span className={styles.price_currency} aria-hidden="true">
                    €
                  </span>
                  <input
                    id="worker-service-cost"
                    name="cost"
                    className={styles.price_input}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="0.00"
                    value={values.cost}
                    onChange={(event) =>
                      setFieldValue('cost', sanitizePriceInput(event.target.value))
                    }
                    onBlur={() => setFieldTouched('cost', true)}
                    onKeyDown={(event) => {
                      if (event.ctrlKey || event.metaKey || event.altKey) return;
                      if (!isPriceKeyAllowed(event.key, values.cost)) {
                        event.preventDefault();
                      }
                    }}
                    onPaste={(event) => {
                      event.preventDefault();
                      const text = event.clipboardData.getData('text');
                      setFieldValue('cost', sanitizePriceInput(text));
                    }}
                  />
                </div>
                {priceError ? (
                  <div className={styles.price_error} role="alert">
                    {priceError}
                  </div>
                ) : null}
              </div>
            </div>
            <div className={clsx(styles.actions_container, styles.footer)}>
              <Button className={styles.confirm_button} variant="contained" type="submit">
                {t('common.actions.save')}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
